import React, { useState, useEffect, useCallback } from 'react';
import { useApp } from '../context/AppContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { CheckCircle2, XCircle, MapPin, Calendar, AlertTriangle, User, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { InstructionGuide } from '../components/layout/InstructionGuide';

const PURPOSE_LABELS = { new_sale_point: 'New Sale Point Addition', growth_existing: 'Growth from Existing', both: 'Both' };
const STATUS_COLORS = { pending_approval: 'bg-amber-100 text-amber-700', approved: 'bg-blue-100 text-blue-700', rejected: 'bg-red-100 text-red-700', in_progress: 'bg-emerald-100 text-emerald-700', completed: 'bg-slate-100 text-slate-700' };

export default function TravelApprovals() {
  const { api, isAdmin, selectedBusiness } = useApp();
  const [plans, setPlans] = useState([]);
  const [users, setUsers] = useState([]);
  const [detailPlan, setDetailPlan] = useState(null);
  const [remarks, setRemarks] = useState('');
  const [activeTab, setActiveTab] = useState('pending');

  const load = useCallback(async () => {
    try {
      const params = {};
      if (selectedBusiness) params.business_id = selectedBusiness;
      const [pRes, uRes] = await Promise.all([api.getTravelPlans(params), api.getUsers()]);
      setPlans(pRes.data.data || pRes.data);
      setUsers(uRes.data.data || uRes.data);
    } catch (e) { console.error(e); }
  }, [api, selectedBusiness]);

  useEffect(() => { load(); }, [load]);

  const getUserName = (userId) => users.find(u => u.id === userId)?.name || userId;
  const getDays = (s, e) => { if (!s || !e) return 0; return Math.max(1, Math.ceil((new Date(e) - new Date(s)) / 86400000) + 1); };

  const handleApprove = async (planId, action) => {
    try {
      await api.approveTravelPlan(planId, action, remarks);
      toast.success(action === 'approve' ? 'Plan approved' : 'Plan rejected');
      setRemarks('');
      load();
      if (detailPlan?.id === planId) {
        const res = await api.getTravelPlan(planId);
        setDetailPlan(res.data);
      }
    } catch (e) { toast.error('Action failed'); }
  };

  if (!isAdmin) {
    return (
      <div data-testid="travel-approvals-page" className="space-y-6">
        <Card className="border border-border/60"><CardContent className="p-12 text-center">
          <p className="text-sm text-slate-400">Admin access only.</p>
        </CardContent></Card>
      </div>
    );
  }

  const pending = plans.filter(p => p.status === 'pending_approval');
  const approved = plans.filter(p => p.status === 'approved');
  const inProgress = plans.filter(p => p.status === 'in_progress');
  const completed = plans.filter(p => p.status === 'completed');
  const rejected = plans.filter(p => p.status === 'rejected');

  const PlanRow = ({ plan }) => {
    const purpose = PURPOSE_LABELS[plan.purpose] || plan.purpose;
    const days = getDays(plan.start_date, plan.end_date);
    const totalExp = (plan.daily_expenses || []).reduce((s, e) => s + (e.breakfast || 0) + (e.lunch || 0) + (e.dinner || 0) + (e.night_stay > 0 ? e.night_stay : 0), 0);
    return (
      <div data-testid={`approval-card-${plan.id}`} onClick={() => { setDetailPlan(plan); setRemarks(''); }}
        className="flex items-center gap-4 p-4 border border-border/40 rounded-lg hover:bg-slate-50 cursor-pointer transition-all">
        <div className="w-10 h-10 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center font-bold text-sm flex-shrink-0">
          {getUserName(plan.user_id)?.charAt(0) || '?'}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="text-sm font-bold text-slate-900 truncate">{getUserName(plan.user_id)}</p>
            <Badge className={`text-[10px] ${STATUS_COLORS[plan.status]}`}>{plan.status.replace('_', ' ')}</Badge>
          </div>
          <p className="text-xs text-slate-500">{purpose} | {plan.start_date} to {plan.end_date} ({days} days)</p>
        </div>
        <div className="text-right flex-shrink-0">
          <p className="text-sm font-bold text-slate-900">Rs.{totalExp.toLocaleString()}</p>
          <p className="text-xs text-slate-400">{(plan.locations || []).length} locations</p>
        </div>
        {plan.deviations?.length > 0 && <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0" />}
      </div>
    );
  };

  return (
    <div data-testid="travel-approvals-page" className="space-y-6 fade-in">
      <div>
        <h1 className="text-3xl font-bold tracking-tight text-slate-900">Travel Approvals</h1>
        <p className="text-sm text-slate-500 mt-1">Review and approve team travel plans</p>
      </div>

      <InstructionGuide
        title="How Travel Approvals work"
        description="Review travel plans submitted by your team. Approve or reject plans with remarks. Track plan progress and deviations."
        steps={[
          'Review pending plans in the Pending tab — click a plan to see full details.',
          'Approve or reject a plan with optional remarks.',
          'Monitor approved and in-progress plans for deviations and expense reports.',
        ]}
        flow={['Review Pending', 'Approve / Reject', 'Monitor Progress', 'View Completed']}
      />

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          { label: 'Pending', count: pending.length, color: 'bg-amber-500' },
          { label: 'Approved', count: approved.length, color: 'bg-blue-500' },
          { label: 'In Progress', count: inProgress.length, color: 'bg-emerald-500' },
          { label: 'Completed', count: completed.length, color: 'bg-slate-500' },
          { label: 'Rejected', count: rejected.length, color: 'bg-red-500' },
        ].map(s => (
          <Card key={s.label} className="border border-border/60"><CardContent className="p-3 text-center">
            <p className="text-2xl font-bold text-slate-900">{s.count}</p>
            <div className="flex items-center justify-center gap-1.5 mt-1">
              <div className={`w-2 h-2 rounded-full ${s.color}`} />
              <p className="text-xs text-slate-500">{s.label}</p>
            </div>
          </CardContent></Card>
        ))}
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-slate-100">
          <TabsTrigger value="pending" className="data-[state=active]:bg-amber-600 data-[state=active]:text-white">
            Pending {pending.length > 0 && <Badge className="ml-1.5 bg-red-500 text-white text-[10px]">{pending.length}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="approved" className="data-[state=active]:bg-amber-600 data-[state=active]:text-white">Approved</TabsTrigger>
          <TabsTrigger value="in-progress" className="data-[state=active]:bg-amber-600 data-[state=active]:text-white">In Progress</TabsTrigger>
          <TabsTrigger value="completed" className="data-[state=active]:bg-amber-600 data-[state=active]:text-white">Completed</TabsTrigger>
          <TabsTrigger value="rejected" className="data-[state=active]:bg-amber-600 data-[state=active]:text-white">Rejected</TabsTrigger>
        </TabsList>
        {[
          { key: 'pending', data: pending },
          { key: 'approved', data: approved },
          { key: 'in-progress', data: inProgress },
          { key: 'completed', data: completed },
          { key: 'rejected', data: rejected },
        ].map(t => (
          <TabsContent key={t.key} value={t.key}>
            {t.data.length === 0 ? (
              <Card className="border border-border/60"><CardContent className="p-12 text-center"><p className="text-sm text-slate-400">No {t.key} plans</p></CardContent></Card>
            ) : (
              <div className="space-y-2">{t.data.map(p => <PlanRow key={p.id} plan={p} />)}</div>
            )}
          </TabsContent>
        ))}
      </Tabs>

      {/* Detail + Approval Dialog */}
      <Dialog open={!!detailPlan} onOpenChange={(o) => !o && setDetailPlan(null)}>
        <DialogContent data-testid="approval-detail-dialog" className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
          {detailPlan && (() => {
            const p = detailPlan;
            const totalExp = (p.daily_expenses || []).reduce((s, e) => s + (e.breakfast || 0) + (e.lunch || 0) + (e.dinner || 0) + (e.night_stay > 0 ? e.night_stay : 0), 0);
            return (
              <>
                <DialogHeader>
                  <div className="flex items-center gap-2">
                    <DialogTitle className="text-lg font-bold">Travel Plan Review</DialogTitle>
                    <Badge className={`${STATUS_COLORS[p.status]}`}>{p.status.replace('_', ' ')}</Badge>
                  </div>
                  <p className="text-sm text-slate-500"><User className="w-3 h-3 inline mr-1" />{getUserName(p.user_id)} | {PURPOSE_LABELS[p.purpose] || p.purpose}</p>
                </DialogHeader>
                <div className="space-y-4 py-2">
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div className="p-3 bg-slate-50 rounded-lg"><p className="text-xs text-slate-400">Dates</p><p className="font-semibold">{p.start_date} to {p.end_date}</p></div>
                    <div className="p-3 bg-slate-50 rounded-lg"><p className="text-xs text-slate-400">Total Expense</p><p className="font-bold text-amber-600">Rs.{totalExp.toLocaleString()}</p></div>
                  </div>

                  {p.start_selfie && (
                    <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                      <p className="text-xs font-bold text-emerald-800 mb-2">Start Selfie</p>
                      <img src={p.start_selfie} alt="Start selfie" className="w-32 h-32 object-cover rounded-lg" />
                      <p className="text-xs text-emerald-600 mt-1">GPS: {p.start_lat?.toFixed(4)}, {p.start_lng?.toFixed(4)} | {p.start_time && new Date(p.start_time).toLocaleString()}</p>
                    </div>
                  )}

                  <div>
                    <h3 className="text-sm font-bold text-slate-700 mb-2">Locations</h3>
                    {(p.locations || []).map((loc, i) => {
                      const hasDeviation = (p.deviations || []).some(d => d.location_index === i);
                      return (
                        <div key={i} className={`p-3 border rounded-lg mb-2 ${hasDeviation ? 'border-amber-300 bg-amber-50' : 'border-border/40'}`}>
                          <p className="text-sm font-semibold text-slate-900">{loc.place_name || `Location ${i + 1}`}</p>
                          <p className="text-xs text-slate-400">Planned: {loc.planned_checkin} - {loc.planned_checkout} ({loc.planned_duration_mins} mins)</p>
                          {loc.actual_checkin && <p className="text-xs text-emerald-600">In: {new Date(loc.actual_checkin).toLocaleTimeString()}</p>}
                          {loc.actual_checkout && <p className="text-xs text-blue-600">Out: {new Date(loc.actual_checkout).toLocaleTimeString()} | {loc.actual_duration_mins} mins</p>}
                          {hasDeviation && <p className="text-xs text-amber-700 font-semibold"><AlertTriangle className="w-3 h-3 inline mr-1" />Deviation</p>}
                        </div>
                      );
                    })}
                  </div>

                  <div>
                    <h3 className="text-sm font-bold text-slate-700 mb-2">Expenses</h3>
                    <div className="border border-border/40 rounded-lg overflow-hidden">
                      <table className="w-full text-xs">
                        <thead><tr className="bg-slate-50 border-b"><th className="p-2 text-left">Day</th><th className="p-2">Breakfast</th><th className="p-2">Lunch</th><th className="p-2">Dinner</th><th className="p-2">Night Stay</th></tr></thead>
                        <tbody>{(p.daily_expenses || []).map((e, i) => (
                          <tr key={i} className="border-b border-slate-50"><td className="p-2 font-semibold">Day {e.day}</td><td className="p-2 text-center">{e.breakfast}</td><td className="p-2 text-center">{e.lunch}</td><td className="p-2 text-center">{e.dinner}</td><td className="p-2 text-center">{e.night_stay >= 0 ? e.night_stay : '-'}</td></tr>
                        ))}</tbody>
                      </table>
                    </div>
                  </div>

                  {(p.expense_bills || []).length > 0 && (
                    <div>
                      <h3 className="text-sm font-bold text-slate-700 mb-2">Uploaded Bills ({p.expense_bills.length})</h3>
                      <div className="flex flex-wrap gap-2">{p.expense_bills.map((b, i) => (
                        <img key={i} src={b} alt={`Bill ${i + 1}`} className="w-20 h-20 object-cover rounded-lg border border-border/40" />
                      ))}</div>
                    </div>
                  )}

                  {p.deviations?.length > 0 && (
                    <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                      <h3 className="text-sm font-bold text-amber-800 mb-1"><AlertTriangle className="w-4 h-4 inline mr-1" />Deviations</h3>
                      {p.deviations.map((d, i) => <p key={i} className="text-xs text-amber-700">Location {d.location_index + 1}: Planned {d.planned_mins} mins, Actual {d.actual_mins} mins</p>)}
                    </div>
                  )}

                  {p.notes && <div><h3 className="text-sm font-bold text-slate-700 mb-1">Notes</h3><p className="text-sm text-slate-600">{p.notes}</p></div>}

                  {p.status === 'pending_approval' && (
                    <div>
                      <Label>Approval Remarks (optional)</Label>
                      <Textarea data-testid="approval-remarks" value={remarks} onChange={e => setRemarks(e.target.value)} placeholder="Add remarks..." className="mt-1" />
                    </div>
                  )}
                </div>

                {p.status === 'pending_approval' && (
                  <DialogFooter>
                    <Button data-testid="reject-plan-btn" variant="outline" className="flex-1 border-red-200 text-red-600 hover:bg-red-50" onClick={() => handleApprove(p.id, 'reject')}>
                      <XCircle className="w-4 h-4 mr-1" /> Reject
                    </Button>
                    <Button data-testid="approve-plan-btn" className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => handleApprove(p.id, 'approve')}>
                      <CheckCircle2 className="w-4 h-4 mr-1" /> Approve
                    </Button>
                  </DialogFooter>
                )}
              </>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}
